import { format, isValid, parseISO } from 'date-fns';
import type { Locale } from 'date-fns';
import type { TFunction } from 'i18next';
import type { MergedScheduledPayment } from 'vopay';

const WEEKDAY_CODE_TO_KEY: Record<string, string> = {
  SU: 'sunday',
  MO: 'monday',
  TU: 'tuesday',
  WE: 'wednesday',
  TH: 'thursday',
  FR: 'friday',
  SA: 'saturday',
};

const BY_DAY_WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

// date-fns' parseISO (unlike the native Date constructor) parses a date-only string as local
// midnight rather than UTC midnight, avoiding the shifts-a-day-back bug in negative-offset
// timezones; it also accepts the space-separated "YYYY-MM-DD HH:mm:ss" shape the API returns.
export function parseLocalDate(value: string): Date | undefined {
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

const ORDINAL_CODE_TO_KEY: Record<string, string> = {
  '1': 'first',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
  '-1': 'last',
};

function weekdayLabel(code: string, tGlobal: TFunction): string {
  const key = WEEKDAY_CODE_TO_KEY[code];
  return key ? tGlobal(`Weekdays.${key}`, { defaultValue: code }) : code;
}

function weekdayListLabel(byDay: string, tGlobal: TFunction): string {
  const codes = byDay.split(',').filter(Boolean);
  const labels = codes.map((code) => weekdayLabel(code, tGlobal));

  if (labels.length <= 1) return labels[0] || '';
  if (labels.length === 2) return `${labels[0]} ${tGlobal('and', { defaultValue: 'and' })} ${labels[1]}`;

  return `${labels.slice(0, -1).join(', ')} ${tGlobal('and', { defaultValue: 'and' })} ${labels[labels.length - 1]}`;
}

// Monthly ByDay is a single ordinal-weekday code, e.g. "3FR" or "-1MO".
function parseOrdinalWeekday(byDay: string): { ordinal: string; weekday: string } | null {
  const match = /^(-?\d+)([A-Z]{2})$/.exec(byDay);
  if (!match) return null;

  return { ordinal: match[1], weekday: match[2] };
}

/**
 * Composes the human-readable "Repeat" string from a schedule's Repeats/Interval/ByDay
 */
export function composeRepeatLabel(
  transaction: Pick<MergedScheduledPayment, 'Repeats' | 'Interval' | 'ByDay' | 'StartDateTime'>,
  t: TFunction,
  tGlobal: TFunction,
  dateLocale?: Locale,
): string {
  const { Repeats, ByDay, StartDateTime } = transaction;
  const interval = transaction.Interval || 1;
  const startDate = StartDateTime ? parseLocalDate(StartDateTime) : undefined;

  switch (Repeats) {
    case 'daily':
      return interval > 1
        ? t('RepeatLabel.dailyInterval', { interval, defaultValue: 'Every {{interval}} days' })
        : t('RepeatLabel.daily', { defaultValue: 'Daily' });

    case 'weekdays':
      return t('RepeatLabel.weekdaysOnly', { defaultValue: 'Every weekday' });

    case 'weekly': {
      // ByDay is only present for a Custom-built weekly rule — a quick "Weekly" preset
      const weekdays = ByDay
        ? weekdayListLabel(ByDay, tGlobal)
        : startDate
          ? weekdayLabel(BY_DAY_WEEKDAY_CODES[startDate.getDay()], tGlobal)
          : '';
      return interval > 1
        ? t('RepeatLabel.weeklyInterval', {
            interval,
            weekdays,
            defaultValue: 'Every {{interval}} weeks on {{weekdays}}',
          })
        : t('RepeatLabel.weekly', { weekdays, defaultValue: 'Weekly on {{weekdays}}' });
    }

    case 'monthly': {
      const ordinalWeekday = ByDay ? parseOrdinalWeekday(ByDay) : null;
      if (!ordinalWeekday) {
        return interval > 1
          ? t('RepeatLabel.monthlyIntervalPlain', { interval, defaultValue: 'Every {{interval}} months' })
          : t('RepeatLabel.monthlyPlain', { defaultValue: 'Monthly' });
      }

      const ordinal = tGlobal(`Ordinals.${ORDINAL_CODE_TO_KEY[ordinalWeekday.ordinal] || 'first'}`, {
        defaultValue: ORDINAL_CODE_TO_KEY[ordinalWeekday.ordinal] || ordinalWeekday.ordinal,
      });
      const weekday = weekdayLabel(ordinalWeekday.weekday, tGlobal);

      return interval > 1
        ? t('RepeatLabel.monthlyInterval', {
            interval,
            ordinal,
            weekday,
            defaultValue: 'Every {{interval}} months on the {{ordinal}} {{weekday}}',
          })
        : t('RepeatLabel.monthly', { ordinal, weekday, defaultValue: 'Monthly on the {{ordinal}} {{weekday}}' });
    }

    case 'annually': {
      const date = startDate ? format(startDate, 'MMMM d', { locale: dateLocale }) : '';
      return interval > 1
        ? t('RepeatLabel.annuallyInterval', { interval, date, defaultValue: 'Every {{interval}} years on {{date}}' })
        : t('RepeatLabel.annually', { date, defaultValue: 'Annually on {{date}}' });
    }

    case 'semi-monthly':
      return t('RepeatLabel.semiMonthly', { defaultValue: 'Semi-Monthly' });

    case 'bi-weekly':
      return t('RepeatLabel.biWeekly', { defaultValue: 'Bi-Weekly' });

    case 'quarterly':
      return t('RepeatLabel.quarterly', { defaultValue: 'Quarterly' });

    case 'semi-annually':
      return t('RepeatLabel.semiAnnually', { defaultValue: 'Semi-Annually' });

    default:
      return Repeats || '';
  }
}

/**
 * Composes the "Ends" value — the schedule's end condition.
 */
export function composeEndsLabel(
  transaction: Pick<MergedScheduledPayment, 'ScheduleEndDate' | 'NumberOfPayments'>,
  t: TFunction,
  dateLocale?: Locale,
): string {
  const endDate = transaction.ScheduleEndDate ? parseLocalDate(transaction.ScheduleEndDate) : undefined;
  if (endDate) {
    return format(endDate, 'MMM d, yyyy', { locale: dateLocale });
  }

  const count = Number(transaction.NumberOfPayments);
  if (count > 0) {
    return t('EndsLabel.afterOccurrences', { count, defaultValue: 'After {{count}} occurrences' });
  }

  return '';
}
